import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { investigationsApi, type OpenManagerAnalysis } from '../api/investigations'
import { formatDateBR } from '../lib/format'
import { ManagerOutcomeForm } from '../components/InvestigationCard'
import { IntelligenceInbox } from '../components/IntelligenceInbox'
import { Spinner } from '../components/Spinner'

interface ClientGroup {
  clientId: string
  clientName: string
  analyses: OpenManagerAnalysis[]
}
interface ManagerGroup {
  managerId: string
  managerName: string
  clients: Map<string, ClientGroup>
  total: number
}

export default function OpenManagerAnalyses() {
  const { data, isLoading } = useQuery({
    queryKey: ['open-analyses'],
    queryFn: () => investigationsApi.open(),
  })
  const analyses = data?.analyses ?? []

  // Agrupa por gerente → cliente (mesmo padrão das ações abertas).
  const groups = new Map<string, ManagerGroup>()
  for (const a of analyses) {
    let g = groups.get(a.managerId)
    if (!g) {
      g = { managerId: a.managerId, managerName: a.managerName, clients: new Map(), total: 0 }
      groups.set(a.managerId, g)
    }
    g.total++
    let cl = g.clients.get(a.clientId)
    if (!cl) {
      cl = { clientId: a.clientId, clientName: a.clientName, analyses: [] }
      g.clients.set(a.clientId, cl)
    }
    cl.analyses.push(a)
  }
  const managerGroups = [...groups.values()].sort((x, y) => y.total - x.total)

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Análises abertas por gerente</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Das últimas 4 semanas. Responda em 1 clique — o comentário é opcional.
          </p>
        </div>
        {!isLoading && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600">
            {analyses.length} aberta{analyses.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <IntelligenceInbox compact />

      {isLoading ? (
        <Spinner />
      ) : managerGroups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhuma análise pendente. 🎉
        </p>
      ) : (
        <div className="space-y-5">
          {managerGroups.map((g) => (
            <section key={g.managerId} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">{g.managerName}</h2>
                <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-700">
                  {g.total} análise{g.total === 1 ? '' : 's'}
                </span>
              </div>
              <div className="space-y-4">
                {[...g.clients.values()].map((c) => (
                  <div key={c.clientId}>
                    <h3 className="mb-1.5 text-sm font-semibold text-slate-700">{c.clientName}</h3>
                    <div className="space-y-3">
                      {c.analyses.map((a) => (
                        <AnalysisCard key={a.id} analysis={a} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function AnalysisCard({ analysis }: { analysis: OpenManagerAnalysis }) {
  const qc = useQueryClient()

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{analysis.title}</p>
          <p className="mt-0.5 text-sm text-slate-500">
            <span className="font-medium">O que investigar: </span>
            {analysis.hypothesis}
          </p>
          <p className="mt-1 text-xs text-slate-400">semana {formatDateBR(analysis.weekStart)}</p>
        </div>
        <Link
          to={`/reports/${analysis.reportId}`}
          className="whitespace-nowrap text-xs text-brand-600 hover:underline"
        >
          Abrir relatório →
        </Link>
      </div>

      <ManagerOutcomeForm
        investigationId={analysis.id}
        onDone={() => qc.invalidateQueries({ queryKey: ['open-analyses'] })}
      />
    </div>
  )
}
